import {
  useCallback,
  useMemo,
  useState,
  memo,
  useRef,
  useEffect,
  type ChangeEvent
} from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { FormField } from "@/components/ui/form-field";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { NamePrefixSelect, CardSubHeader } from "@/components/ui";
import { MapLocationDialog, type MapLocation } from "@/components/maps";
import { type PracticeRegistrationData } from "@/domain";
import type { UserInfo } from "@/types/core";
import { cn, components, layout, typography } from "@/utilities/tailwind-utils";
import {
  validatePhoneNumber,
  formatPhoneNumber
} from "@/utilities/validation-utils";
import {
  useAvailableStates,
  useAvailableCities,
  getAllCountries,
  resolveRegistrationAddress
} from "@/utilities/location-utils";
import { toast } from "sonner";

// ============================================================================
// PRACTICE REGISTRATION TYPES
// ============================================================================

interface PracticeRegistrationProps {
  data: PracticeRegistrationData;
  userInfo: UserInfo;
  onSubmit: (data: PracticeRegistrationData) => void;
  onBack: () => void;
  onCancel: () => void;
}

// ============================================================================
// PRACTICE REGISTRATION COMPONENT
// ============================================================================

export const PracticeRegistration = memo(function PracticeRegistration({
  data,
  userInfo,
  onSubmit,
  onBack,
  onCancel
}: PracticeRegistrationProps) {
  // Initialize form data with memoized initial state - ensure all values are defined
  const initialFormData = useMemo<PracticeRegistrationData>(
    () => ({
      userId: userInfo.userId || data.userId || "",
      email: userInfo.email || data.email || "",
      namePrefix: data.namePrefix || "Dr.",
      firstName: userInfo.firstName || data.firstName || "",
      middleName: userInfo.middleName || data.middleName || "",
      lastName: userInfo.lastName || data.lastName || "",
      phone: data.phone || "",
      designation: data.designation || "",
      practiceName: data.practiceName || "",
      website: data.website || "",
      description: data.description || "",
      taxId: data.taxId || "",
      licenseNumber: data.licenseNumber || "",
      branchName: data.branchName || "",
      addressCountry: data.addressCountry || "Canada",
      addressState: data.addressState || "",
      addressCity: data.addressCity || "",
      addressStreet: data.addressStreet || "",
      addressPostalCode: data.addressPostalCode || "",
      addressLongitude: data.addressLongitude,
      addressLatitude: data.addressLatitude
    }),
    [data, userInfo]
  );

  // Split form states to prevent unnecessary re-renders
  const [namePrefix, setNamePrefix] = useState(
    () => initialFormData.namePrefix
  );
  const [firstName, setFirstName] = useState(() => initialFormData.firstName);
  const [middleName, setMiddleName] = useState(
    () => initialFormData.middleName
  );
  const [lastName, setLastName] = useState(() => initialFormData.lastName);
  const [phone, setPhone] = useState(() => initialFormData.phone);
  const [designation, setDesignation] = useState(
    () => initialFormData.designation
  );
  const [practiceName, setPracticeName] = useState(
    () => initialFormData.practiceName
  );
  const [website, setWebsite] = useState(() => initialFormData.website);
  const [description, setDescription] = useState(
    () => initialFormData.description
  );
  const [taxId, setTaxId] = useState(() => initialFormData.taxId);
  const [licenseNumber, setLicenseNumber] = useState(
    () => initialFormData.licenseNumber
  );
  const [branchName, setBranchName] = useState(
    () => initialFormData.branchName
  );
  const [addressStreet, setAddressStreet] = useState(
    () => initialFormData.addressStreet
  );
  const [addressPostalCode, setAddressPostalCode] = useState(
    () => initialFormData.addressPostalCode
  );
  const [addressCountry, setAddressCountry] = useState(
    () => initialFormData.addressCountry
  );
  const [addressState, setAddressState] = useState(
    () => initialFormData.addressState
  );
  const [addressCity, setAddressCity] = useState(
    () => initialFormData.addressCity
  );
  const [selectedCountry, setSelectedCountry] = useState(
    () => data.addressCountry || ""
  );
  const [phoneError, setPhoneError] = useState<string>("");
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [selectedLocation, setSelectedLocation] = useState<MapLocation | null>(
    () =>
      initialFormData.addressLatitude != null &&
        initialFormData.addressLongitude != null
        ? {
          lat: initialFormData.addressLatitude,
          lng: initialFormData.addressLongitude
        }
        : null
  );

  // Stable ref to hold complete form state for validation and submission
  const formDataRef = useRef<PracticeRegistrationData>(initialFormData);

  // Sync formDataRef when initialFormData changes
  useEffect(() => {
    formDataRef.current = initialFormData;
  }, [initialFormData]);

  // Validation function - works with stable ref
  const validateFields = useCallback(() => {
    const currentFormData = formDataRef.current;
    const requiredFields = [
      currentFormData.namePrefix?.trim(),
      currentFormData.firstName?.trim(),
      currentFormData.lastName?.trim(),
      currentFormData.phone?.trim(),
      currentFormData.designation?.trim(),
      currentFormData.practiceName?.trim(),
      currentFormData.website?.trim(),
      currentFormData.taxId?.trim(),
      currentFormData.licenseNumber?.trim(),
      currentFormData.branchName?.trim(),
      currentFormData.addressCountry?.trim(),
      currentFormData.addressState?.trim(),
      currentFormData.addressCity?.trim(),
      currentFormData.addressStreet?.trim(),
      currentFormData.addressPostalCode?.trim()
    ];

    // Check if all required fields are filled
    const allFieldsFilled = requiredFields.every(
      (field) => field && field !== ""
    );

    // Check phone validation
    const phoneValidation = validatePhoneNumber(currentFormData.phone || "", {
      required: true,
      countryCode: currentFormData.addressCountry === "Canada" ? "CA" : "US"
    });

    return allFieldsFilled && phoneValidation.isValid;
  }, []);

  // Debounced validation to update form validity state
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setIsFormValid(validateFields());
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [
    namePrefix,
    firstName,
    middleName,
    lastName,
    phone,
    designation,
    practiceName,
    website,
    taxId,
    licenseNumber,
    branchName,
    addressCountry,
    addressState,
    addressCity,
    addressStreet,
    addressPostalCode,
    phoneError,
    validateFields
  ]);

  // Optimized field handlers - individual state updates with ref sync

  const handleNamePrefixChange = useCallback((value: string) => {
    setNamePrefix(value);
    formDataRef.current = { ...formDataRef.current, namePrefix: value };
  }, []);

  const handleFirstNameChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setFirstName(value);
      formDataRef.current = { ...formDataRef.current, firstName: value };
    },
    []
  );

  const handleMiddleNameChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setMiddleName(value);
      formDataRef.current = { ...formDataRef.current, middleName: value };
    },
    []
  );

  const handleLastNameChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setLastName(value);
      formDataRef.current = { ...formDataRef.current, lastName: value };
    },
    []
  );

  const handlePhoneChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      const countryCode = addressCountry === "Canada" ? "CA" : "US";

      // Format the phone number as user types
      const formattedValue = formatPhoneNumber(value, countryCode);
      setPhone(formattedValue);
      formDataRef.current = { ...formDataRef.current, phone: formattedValue };

      // Validate phone number
      const phoneValidation = validatePhoneNumber(formattedValue, {
        required: true,
        countryCode
      });
      setPhoneError(phoneValidation.isValid ? "" : phoneValidation.error || "");
    },
    [addressCountry]
  );

  const handleDesignationChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setDesignation(value);
      formDataRef.current = { ...formDataRef.current, designation: value };
    },
    []
  );

  const handlePracticeNameChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setPracticeName(value);
      formDataRef.current = { ...formDataRef.current, practiceName: value };
    },
    []
  );

  const handleBranchNameChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setBranchName(value);
      formDataRef.current = { ...formDataRef.current, branchName: value };
    },
    []
  );

  const handleTaxIdChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTaxId(value);
    formDataRef.current = { ...formDataRef.current, taxId: value };
  }, []);

  const handleLicenseNumberChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setLicenseNumber(value);
      formDataRef.current = { ...formDataRef.current, licenseNumber: value };
    },
    []
  );

  const handleWebsiteChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setWebsite(value);
      formDataRef.current = { ...formDataRef.current, website: value };
    },
    []
  );

  const handleDescriptionChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value;
      setDescription(value);
      formDataRef.current = { ...formDataRef.current, description: value };
    },
    []
  );

  const handleAddressStreetChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setAddressStreet(value);
      formDataRef.current = { ...formDataRef.current, addressStreet: value };
    },
    []
  );

  const handleAddressPostalCodeChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setAddressPostalCode(value);
      formDataRef.current = {
        ...formDataRef.current,
        addressPostalCode: value
      };
    },
    []
  );

  const handleAddressCountryChange = useCallback((value: string) => {
    setAddressCountry(value);
    setAddressState("");
    setAddressCity("");
    setSelectedCountry(value);
    formDataRef.current = {
      ...formDataRef.current,
      addressCountry: value,
      addressState: "",
      addressCity: ""
    };
  }, []);

  const handleAddressStateChange = useCallback((value: string) => {
    setAddressState(value);
    setAddressCity("");
    formDataRef.current = {
      ...formDataRef.current,
      addressState: value,
      addressCity: ""
    };
  }, []);

  const handleAddressCityChange = useCallback((value: string) => {
    setAddressCity(value);
    formDataRef.current = { ...formDataRef.current, addressCity: value };
  }, []);

  const handleLocationSelect = useCallback((location: MapLocation) => {
    setSelectedLocation(location);
    const resolved = resolveRegistrationAddress(location, {
      addressStreet: formDataRef.current.addressStreet || "",
      addressCity: formDataRef.current.addressCity || "",
      addressState: formDataRef.current.addressState || "",
      addressPostalCode: formDataRef.current.addressPostalCode || "",
      addressCountry: formDataRef.current.addressCountry || ""
    });
    setAddressStreet(resolved.addressStreet);
    setAddressPostalCode(resolved.addressPostalCode);
    setAddressCountry(resolved.addressCountry);
    setSelectedCountry(resolved.addressCountry);
    setAddressState(resolved.addressState);
    setAddressCity(resolved.addressCity);
    formDataRef.current = {
      ...formDataRef.current,
      ...resolved,
      addressLatitude: location.lat,
      addressLongitude: location.lng
    };
  }, []);

  // Optimized submit handler
  const handleSubmit = useCallback(() => {
    // Basic validation
    if (!validateFields()) {
      toast.error("Please fill in all required fields.");
      return;
    }
    // Submit form data from ref
    onSubmit(formDataRef.current);
  }, [onSubmit, validateFields]);

  // Get available states and cities using utilities
  const availableStates = useAvailableStates(selectedCountry);
  const availableCities = useAvailableCities(selectedCountry, addressState);
  const countries = useMemo(() => getAllCountries(), []);

  // Render

  return (
    <div className="space-y-8">
      {/* PRACTICE INFORMATION SECTION */}
      <div className="space-y-6 mb-2">
        <CardSubHeader title="PRACTICE INFORMATION" />

        <div className="space-y-4">
          {/* PRACTICE NAME AND WEBSITE */}
          <div className={cn(layout.gridCols1MdCols2)}>
            {/* PRACTICE NAME */}
            <FormField
              id="practiceName"
              label="Legal Name"
              value={practiceName}
              onChange={handlePracticeNameChange}
              placeholder="e.g. Smile Care Clinic"
              maxLength={255}
              required
              className="col-span-1"
            />

            {/* WEBSITE */}
            <FormField
              id="website"
              label="Website"
              value={website}
              onChange={handleWebsiteChange}
              placeholder="e.g. www.smilecareclinic.com"
              maxLength={255}
              required
              className="col-span-1"
            />
          </div>

          {/* TAX ID AND LICENSE NUMBER */}
          <div className={cn(layout.gridCols1MdCols2)}>
            {/* TAX ID */}
            <FormField
              id="taxId"
              label="Tax Id"
              value={taxId}
              onChange={handleTaxIdChange}
              placeholder="Enter tax id"
              maxLength={50}
              required
              className="col-span-1"
            />

            {/* LICENSE NUMBER */}
            <FormField
              id="licenseNumber"
              label="License Number"
              value={licenseNumber}
              onChange={handleLicenseNumberChange}
              placeholder="Enter license number"
              maxLength={50}
              required
              className="col-span-1"
            />
          </div>

          {/* DESCRIPTION */}
          <div className="space-y-2">
            <Label htmlFor="description">Practice Bio</Label>
            <Textarea
              id="description"
              value={description}
              onChange={handleDescriptionChange}
              placeholder="e.g. A family-friendly dental practice providing general, preventative, and cosmetic care in a welcoming environment."
              maxLength={1000}
              className="min-h-[100px]"
            />
            <div className="text-right text-xs text-gray-500">
              {description?.length || 0}/1000
            </div>
          </div>
        </div>
      </div>

      {/* MAIN BRANCH INFORMATION SECTION */}
      <div className="space-y-6">
        <CardSubHeader title="MAIN BRANCH" />

        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Add your main branch details to help professionals find and apply
            for nearby shifts. More branches can be added later in settings.
          </p>

          {/* DISPLAY NAME */}
          <FormField
            id="practiceBranchName"
            label="Branch Name"
            value={branchName}
            onChange={handleBranchNameChange}
            placeholder="e.g. Downtown Branch"
            maxLength={100}
            required
          />

          {/* ADDRESS */}
          <div className="space-y-2">
            <Label htmlFor="address">Address</Label>
            <div className={layout.flexCenterGap2}>
              <div className={layout.flex1}>
                <FormField
                  id="address"
                  value={addressStreet}
                  onChange={handleAddressStreetChange}
                  placeholder="Start typing your address ... e.g. 123 Main St"
                  maxLength={255}
                  required
                  className="flex-1"
                />
              </div>
              <MapLocationDialog
                onLocationSelect={handleLocationSelect}
                initialLocation={selectedLocation}
                defaultCenter={
                  selectedLocation
                    ? { lat: selectedLocation.lat, lng: selectedLocation.lng }
                    : undefined
                }
              />
            </div>
          </div>

          {/* COUNTRY AND STATE */}
          <div className={cn(layout.gridCols1MdCols2)}>
            {/* COUNTRY */}
            <div className="space-y-2">
              <Label htmlFor="country">Country</Label>
              <Select
                value={addressCountry}
                onValueChange={handleAddressCountryChange}
                required
              >
                <SelectTrigger
                  className={cn(components.selectTriggerFormField)}
                >
                  <SelectValue placeholder="Select country" />
                </SelectTrigger>
                <SelectContent>
                  {countries.map((country, idx) => (
                    <SelectItem key={country.name + idx} value={country.name}>
                      {country.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* STATE */}
            <div className="space-y-2">
              <Label htmlFor="state">State</Label>
              <Select
                value={addressState}
                onValueChange={handleAddressStateChange}
                disabled={!addressCountry}
                required
              >
                <SelectTrigger
                  className={cn(components.selectTriggerFormField)}
                >
                  <SelectValue placeholder="Select state" />
                </SelectTrigger>
                <SelectContent>
                  {availableStates.map((state) => (
                    <SelectItem
                      key={state.code + state.name}
                      value={state.code}
                    >
                      {state.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* CITY AND ZIP CODE */}
          <div className={cn(layout.gridCols1MdCols2)}>
            <div className="space-y-2">
              <Label htmlFor="city">City</Label>
              <Select
                value={addressCity}
                onValueChange={handleAddressCityChange}
                disabled={!addressState}
                required
              >
                <SelectTrigger
                  className={cn(components.selectTriggerFormField)}
                >
                  <SelectValue placeholder="Select city" />
                </SelectTrigger>
                <SelectContent>
                  {availableCities.map((city) => (
                    <SelectItem key={city.code} value={city.name}>
                      {city.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* ZIP CODE */}
            <FormField
              id="zipCode"
              label="ZIP Code"
              value={addressPostalCode}
              onChange={handleAddressPostalCodeChange}
              placeholder="Enter ZIP code"
              maxLength={20}
              required
            />
          </div>
        </div>
      </div>

      {/* ADMIN INFORMATION SECTION */}
      <div className="space-y-6 mb-0">
        <CardSubHeader title="ADMIN INFORMATION" />

        <div className="space-y-4">
          {/* NAME PREFIX AND FIRST NAME */}
          <div className={cn(layout.flexRowGap2ColSpan1)}>
            {/* Name Prefix */}
            <NamePrefixSelect
              value={namePrefix}
              onChange={handleNamePrefixChange}
              required
            />
            {/* FIRST NAME */}
            <FormField
              id="firstName"
              label="First Name"
              value={firstName}
              onChange={handleFirstNameChange}
              placeholder="e.g. John"
              maxLength={20}
              required
              className="w-full"
            />
          </div>

          {/* MIDDLE NAME AND LAST NAME */}
          <div className={cn(layout.gridCols1MdCols2)}>
            {/* MIDDLE NAME */}
            <FormField
              id="middleName"
              label="Middle Name"
              value={middleName}
              onChange={handleMiddleNameChange}
              placeholder="Middle Name"
              maxLength={25}
              className="col-span-1"
            />
            {/* LAST NAME */}
            <FormField
              id="lastName"
              label="Last Name"
              value={lastName}
              onChange={handleLastNameChange}
              placeholder="e.g. Doe"
              maxLength={20}
              required
              className="col-span-1"
            />
          </div>

          {/* PHONE NUMBER AND EMAIL */}
          <div className={cn(layout.gridCols1MdCols2)}>
            {/* PHONE NUMBER */}
            <div className="col-span-1">
              <FormField
                id="phoneNumber"
                label="Mobile number"
                value={phone}
                onChange={handlePhoneChange}
                placeholder="+1 412 869 4134"
                type="tel"
                maxLength={20}
                required
                className={phoneError ? "border-destructive" : ""}
              />
              {phoneError && (
                <p className={cn(typography.phoneErrorMessage)}>{phoneError}</p>
              )}
            </div>
            {/* EMAIL */}
            <FormField
              id="email"
              label="Email address"
              value={initialFormData.email}
              placeholder="e.g. johndoe@email.com"
              disabled
              required
              className="col-span-1"
            />
          </div>

          {/* DESIGNATION */}
          <FormField
            id="designation"
            label="Designation"
            value={designation}
            onChange={handleDesignationChange}
            placeholder="e.g. Practice Manager"
            maxLength={255}
            required
          />
        </div>
      </div>

      {/* ACTION BUTTONS */}
      <div className="flex justify-between pt-6">
        <Button variant="outline" onClick={onBack} className="px-6">
          Back
        </Button>
        <div className="flex space-x-3">
          <Button variant="outline" onClick={onCancel} className="px-6">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            className={cn(components.buttonContinue)}
            disabled={!isFormValid}
          >
            CONTINUE
          </Button>
        </div>
      </div>
    </div>
  );
});
